// useLayerFavorites.ts - Shared hook for all layer favorite actions.
// Provides three generic methods: isFavorite, toggleFavorite, getFavoriteCount.
// No type-specific wrappers — callers pass the LayerType directly.

import { useCallback, useMemo, useState } from 'react';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useLayerOrderingMirror } from '@ais-ui/user-settings';
import {
  isLayerFavorite,
  addFavoriteLayer,
  removeFavoriteLayer,
  getLayerOrdering,
} from './layerFavoriteCore';
import type { LayerType } from './layerFavoriteCore';

interface ToggleOptions {
  /** Custom toast message when adding to startup. */
  addMessage?: string;
  /** Custom toast message when removing from startup. */
  removeMessage?: string;
  /** Custom toast message on failure. */
  errorMessage?: string;
}

export const useLayerFavorites = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const mirrorOrdering = useLayerOrderingMirror();
  const [togglingLayerId, setTogglingLayerId] = useState<string | null>(null);

  /** Check if any layer is a startup favorite. */
  const isFavorite = useCallback(
    (layerId: string) => isLayerFavorite(settings, layerId),
    [settings],
  );

  /** Check if a specific layer is currently being toggled. */
  const isToggling = useCallback(
    (layerId: string) => togglingLayerId === layerId,
    [togglingLayerId],
  );

  /** Toggle a layer's favorite status with toast notification. */
  const toggleFavorite = useCallback(
    (
      layerId: string,
      layerType: LayerType,
      label: string,
      opts?: ToggleOptions,
    ) => {
      const isFav = isLayerFavorite(settings, layerId);
      const settingsToSave = isFav
        ? removeFavoriteLayer(settings, layerId, layerType)
        : addFavoriteLayer(settings, layerId, layerType);

      const successMsg = isFav
        ? (opts?.removeMessage ?? `Removed ${label} from startup`)
        : (opts?.addMessage ?? `Added ${label} to startup`);

      const errorMsg =
        opts?.errorMessage ?? `Failed to update startup for ${label}`;

      setTogglingLayerId(layerId);

      mutateSettings(settingsToSave, {
        onSuccess: () => {
          // Blob is authoritative; mirror the new ordering slice to the service.
          mirrorOrdering(getLayerOrdering(settingsToSave));
          toast({
            title: messages.genericToastTitleSuccess,
            description: successMsg,
          });
        },
        onError: () => {
          toast({
            title: messages.genericToastTitleError,
            description: errorMsg,
            variant: 'destructive',
          });
        },
        onSettled: () => {
          setTogglingLayerId(null);
        },
      });
    },
    [settings, mutateSettings, toast, messages, mirrorOrdering],
  );

  /** Count how many items in a list are favorites. */
  const getFavoriteCount = useCallback(
    (items: { id: string }[]) =>
      items.filter((item) => isLayerFavorite(settings, item.id)).length,
    [settings],
  );

  return useMemo(
    () => ({ isFavorite, isToggling, toggleFavorite, getFavoriteCount }),
    [isFavorite, isToggling, toggleFavorite, getFavoriteCount],
  );
};
